import Accordion from "@/common/components/Accordion"
import Button from "@/common/components/Button"
import { CurrencyTextField } from "@/common/components/CurrencyTextField"
import Select from "@/common/components/Select"
import TextField from "@/common/components/TextField"
import { UnitTextField } from '@/common/components/UnitTextField'
import useDebounce from "@/common/hooks/useDebounce"
import { Mapper } from "@/common/services/mapper"
import { Option } from '@/common/types/option'
import { formatCurrency, formatPercentage } from '@/common/utils/formatters'
import { unFormatCurrency, unFormatPercentage } from "@/common/utils/unFormatters"
import { useCreateEstablishmentLeadContractFee } from "@/features/establishmentLeadsFunnel/hooks/useCreateEstablishmentLeadContractFee"
import { useDeleteEstablishmentLeadContractFee } from "@/features/establishmentLeadsFunnel/hooks/useDeleteEstablishmentLeadContractFee"
import { useUpdateEstablishmentLeadContractFee } from "@/features/establishmentLeadsFunnel/hooks/useUpdateEstablishmentLeadContractFee"
import { EstablishmentLeadsFunnelUpdateContractValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { ContractData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { useFeeTemplates } from "@/features/feeTemplates/hooks/useFeeTemplates"
import { FeeTemplateData } from "@/features/feeTemplates/types/feeTemplates.type"
import { useQueryClient } from "@tanstack/react-query"
import { XIcon } from "lucide-react"
import { ChangeEvent, useState } from 'react'
import { Control, Controller, FieldErrors, useFieldArray, UseFormWatch } from "react-hook-form"
import Checkbox from '@/common/components/Checkbox'

interface ContractFeesSectionProps {
  establishmentLeadId?: string
  contract?: ContractData
  control: Control<EstablishmentLeadsFunnelUpdateContractValidationSchema, any>
  watch: UseFormWatch<EstablishmentLeadsFunnelUpdateContractValidationSchema>
  errors: FieldErrors<EstablishmentLeadsFunnelUpdateContractValidationSchema>
  disableFields?: boolean
}

export const ContractFeesSection = ({
  establishmentLeadId,
  contract,
  control,
  watch,
  errors,
  disableFields,
}: ContractFeesSectionProps) => {
  const queryClient = useQueryClient()

  const [contractFeeOptionToAppend, setContractFeeOptionToAppend] = useState<Option>()
  const [contractFeeIndexToRemove, setContractFeeIndexToRemove] = useState<number>()

  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const { data: feeTemplateOptions } = useFeeTemplates({
    filters: {
      search: searchFeeTemplate,
      category: 'establishment',
    },
    mapper: Mapper.mapToOptions<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    mutate: createEstablishmentLeadContractFee,
    isPending: createEstablishmentLeadContractFeeIsPending,
    isError: createEstablishmentLeadContractFeeIsError,
  } = useCreateEstablishmentLeadContractFee({
    establishmentLeadId,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })

      if (contractFeeOptionToAppend) {
        const createdContractFee = response.data.data?.contract?.contract_fees?.find((contractFee) => contractFee.fee_template.id === contractFeeOptionToAppend.value);

        appendContractFee({
          id: createdContractFee?.id || '',
          value: createdContractFee?.fee_template?.calculate_as === 'percentage' ? formatPercentage(createdContractFee.value) : formatCurrency(Number(createdContractFee?.value)),
          fee_template: {
            id: createdContractFee?.fee_template?.id,
            display_name: createdContractFee?.fee_template?.display_name,
            calculate_as: createdContractFee?.fee_template?.calculate_as,
          }
        })

        setContractFeeOptionToAppend(undefined)
      }
    },
    onError: () => {

    },
  })

  const {
    mutate: updateEstablishmentLeadContractFee,
    isPending: updateContractFeeIsPending
  } = useUpdateEstablishmentLeadContractFee({
    establishmentLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })
    }
  })

  const submit = ({
    id,
    value,
    calculate_as,
    main_fee,
  }: {
    id: string
    value: string
    calculate_as: string
    main_fee?: boolean
  }) => {
    if (!/R\$|%/.test(value)) {
      return
    }

    const unFormatFunction = calculate_as === 'percentage' ? unFormatPercentage : unFormatCurrency
    value = `${unFormatFunction(value)}`

    updateEstablishmentLeadContractFee({ id, value, main_fee })
  }

  const submitMainFee = ({ id, main_fee }: { id: string; main_fee: boolean }) => {
    updateEstablishmentLeadContractFee({ id, main_fee })
  }

  const {
    mutate: deleteEstablishmentLeadContactFee,
    isError: deleteEstablishmentLeadContactFeeIsError,
    isPending: deleteEstablishmentLeadContactFeeIsPending,
  } = useDeleteEstablishmentLeadContractFee({
    establishmentLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })

      if (contractFeeIndexToRemove !== undefined) {
        removeContractFee(contractFeeIndexToRemove)

        setContractFeeIndexToRemove(undefined)
      }
    },
  })

  const {
    append: appendContractFee,
    remove: removeContractFee,
  } = useFieldArray({
    control,
    name: 'contract_fees',
  })

  const isServerError = (createEstablishmentLeadContractFeeIsError || deleteEstablishmentLeadContactFeeIsError)

  const isPending = updateContractFeeIsPending || deleteEstablishmentLeadContactFeeIsPending || createEstablishmentLeadContractFeeIsPending

  return (
    <Accordion
      name="contract_fees_section"
      head={<p>Regras e Tarifas</p>}
      triggerDisabled={updateContractFeeIsPending}
      body={
        <div className="flex flex-col gap-4">
          {isServerError && (
            <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
          )}

          <Select
            label="Regras de preço"
            defaultValue={{ label: 'Dinâmica', value: 'dynamic' }}
            options={[{ label: 'Dinâmica', value: 'dynamic' }]}
            isDisabled
          />

          <p>
            Tarifas padrão <span className="text-danger-soft"> *</span>
          </p>

          <Select
            name="add_contract_fee_template"
            placeholder="Pesquisar Tarifa"
            value={null}
            onChange={(newValue) => {
              setContractFeeOptionToAppend(newValue as Option)
              createEstablishmentLeadContractFee(newValue as Option)
            }}
            options={
              feeTemplateOptions?.filter((feeTemplate) => {
                return !contract?.contract_fees?.some(({ fee_template }) => fee_template.id === feeTemplate.value)
              }) || []
            }
            onInputChange={(value) => setSearchFeeTemplate(value)}
            isDisabled={createEstablishmentLeadContractFeeIsPending || disableFields}
          />

          {watch('contract_fees')?.map(({ id, fee_template, value, main_fee }, index) => (
            <div key={fee_template.id} className="flex gap-2">
              <Button
                type="button"
                variant="outline-error"
                className="size-[3.25rem] mt-5"
                onClick={() => {
                  setContractFeeIndexToRemove(index);
                  deleteEstablishmentLeadContactFee(id);
                }}
                disabled={isPending || disableFields}
              >
                <XIcon />
              </Button>

              <div className="w-1/2 flex flex-col gap-y-2">
                <TextField
                  label="Categoria"
                  defaultValue={fee_template.display_name}
                  disabled
                />

                {fee_template.calculate_as === 'percentage' && (
                  <Checkbox
                    name={`contract_fees.${index}.main_fee`}
                    label="Tarifa principal"
                    className="gap-2"
                    labelClassName="text-[10px] font-light italic"
                    defaultChecked={!!main_fee}
                    errorMessage={errors?.contract_fees?.[index]?.main_fee?.message || undefined}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      submitMainFee({
                        id: id,
                        main_fee: event.target.checked,
                      })
                    }}
                    disabled={updateContractFeeIsPending || disableFields}
                  />
                )}
              </div>

              <Controller
                name={`contract_fees.${index}.value`}
                control={control}
                disabled={updateContractFeeIsPending || disableFields}
                render={({ field }) => fee_template.calculate_as === 'fixed' ? (
                  <CurrencyTextField
                    label="Valor"
                    className="w-1/2"
                    {...field}
                    value={value}
                    onBlur={(e) => {
                      submit({
                        id,
                        value: e.target.value,
                        calculate_as: fee_template.calculate_as || '',
                      })
                    }
                    }
                    errorMessage={errors?.contract_fees?.[index]?.value?.message || undefined}
                  />
                ) : (
                  <UnitTextField
                    unit="%"
                    decimals={2}
                    minValue={-100}
                    maxValue={100}
                    allowNegative
                    label="Valor"
                    className="w-1/2"
                    {...field}
                    value={value}
                    errorMessage={errors?.contract_fees?.[index]?.value?.message || undefined}
                    onBlur={(e) => {
                      submit({
                        id,
                        value: e.target.value,
                        calculate_as: fee_template.calculate_as || '',
                      })
                    }}
                  />
                )}
              />
            </div>
          ))}
        </div>
      }
    />
  )
}
